import React, { useEffect, useState } from "react";
import SettingsService from "../../services/SettingsService";
import {
  DEFAULT_LESSON_TYPES_SETTING,
  generateClassTypeId,
  normalizeLessonTypesSetting,
} from "../common/lessonTypes";

const DEFAULT_PAYMENT_REMINDER_RULES = {
  id: "paymentReminderRules",
  enabled: true,
  firstOverdueReminderDaysAfterDueDate: 2,
  nextOverdueReminderDaysAfterPrevious: 7,
  maxOverdueReminders: 3,
};

const DEFAULT_SCHOOL_CONTACT = {
  id: "schoolContact",
  email: "szkola@extra-english.pl",
  phone: "(+48) 575 400 141",
  address: "Wypoczynkowa 43, 62-081 Baranowo",
  mapsUrl: "https://www.google.com/maps/search/?api=1&query=Extra%20English%20Wypoczynkowa%2043%2062-081%20Baranowo",
};

const DEFAULT_CANCELLATION_POLICY = {
  id: "cancellationPolicy",
  enabled: true,
  cancellationCutoffHours: 24,
};

const DEFAULT_DASHBOARD_PREFERENCES = {
  id: "dashboardPreferences",
  returnStateTtlHours: 6,
};

const DEFAULT_PACKAGE_POLICY = {
  id: "packagePolicy",
  pendingPaymentDeadlineDays: 7,
  pendingPaymentReminderDaysBeforeDue: 2,
  endingSoonThresholdUnits: 2,
  emailStudentOnEndingPackage: false,
};

function byId(settings, id, fallback) {
  return settings.find(setting => setting.id === id) || fallback;
}

function toPositiveInputValue(value) {
  return Number.isInteger(Number(value)) && Number(value) > 0 ? Number(value) : 1;
}

const DEFAULT_NEW_LESSON_TYPE = {
  category: "I",
  durationMinutes: 90,
  name: "",
  minStudents: 1,
  maxStudents: 1,
};

const Settings = () => {
  const [paymentRules, setPaymentRules] = useState(DEFAULT_PAYMENT_REMINDER_RULES);
  const [schoolContact, setSchoolContact] = useState(DEFAULT_SCHOOL_CONTACT);
  const [cancellationPolicy, setCancellationPolicy] = useState(DEFAULT_CANCELLATION_POLICY);
  const [dashboardPreferences, setDashboardPreferences] = useState(DEFAULT_DASHBOARD_PREFERENCES);
  const [packagePolicy, setPackagePolicy] = useState(DEFAULT_PACKAGE_POLICY);
  const [lessonTypes, setLessonTypes] = useState(DEFAULT_LESSON_TYPES_SETTING);
  const [newLessonType, setNewLessonType] = useState(DEFAULT_NEW_LESSON_TYPE);
  const [loading, setLoading] = useState(true);
  const [savingSection, setSavingSection] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    SettingsService.getSettings()
      .then((settings) => {
        setPaymentRules(byId(settings, "paymentReminderRules", DEFAULT_PAYMENT_REMINDER_RULES));
        setSchoolContact(byId(settings, "schoolContact", DEFAULT_SCHOOL_CONTACT));
        setCancellationPolicy(byId(settings, "cancellationPolicy", DEFAULT_CANCELLATION_POLICY));
        setDashboardPreferences(byId(settings, "dashboardPreferences", DEFAULT_DASHBOARD_PREFERENCES));
        setPackagePolicy(byId(settings, "packagePolicy", DEFAULT_PACKAGE_POLICY));
        setLessonTypes(normalizeLessonTypesSetting(byId(settings, "lessonTypes", DEFAULT_LESSON_TYPES_SETTING)));
        setErrorMessage("");
      })
      .catch(() => {
        setErrorMessage("Nie udalo sie wczytac ustawien.");
      })
      .finally(() => setLoading(false));
  }, []);

  const handlePaymentChange = (event) => {
    const { name, value, type, checked } = event.target;
    setPaymentRules({
      ...paymentRules,
      [name]: type === "checkbox" ? checked : toPositiveInputValue(value),
    });
  };

  const handleSchoolContactChange = (event) => {
    const { name, value } = event.target;
    setSchoolContact({
      ...schoolContact,
      [name]: value,
    });
  };

  const handleCancellationPolicyChange = (event) => {
    const { name, value, type, checked } = event.target;
    setCancellationPolicy({
      ...cancellationPolicy,
      [name]: type === "checkbox" ? checked : toPositiveInputValue(value),
    });
  };

  const handleDashboardPreferencesChange = (event) => {
    const { name, value } = event.target;
    setDashboardPreferences({
      ...dashboardPreferences,
      [name]: toPositiveInputValue(value),
    });
  };

  const handlePackagePolicyChange = (event) => {
    const { name, value, type, checked } = event.target;
    setPackagePolicy({
      ...packagePolicy,
      [name]: type === "checkbox" ? checked : toPositiveInputValue(value),
    });
  };

  const handleLessonTypeChange = (index, field, value) => {
    setLessonTypes({
      ...lessonTypes,
      types: lessonTypes.types.map((type, currentIndex) => {
        if (currentIndex !== index) return type;
        if (field === "status") return { ...type, status: value ? "ACTIVE" : "INACTIVE" };
        if (["durationMinutes", "minStudents", "maxStudents"].includes(field)) return { ...type, [field]: toPositiveInputValue(value) };
        return { ...type, [field]: value };
      }),
    });
  };

  const handleNewLessonTypeChange = (event) => {
    const { name, value } = event.target;
    setNewLessonType({
      ...newLessonType,
      [name]: ["durationMinutes", "minStudents", "maxStudents"].includes(name)
        ? toPositiveInputValue(value)
        : value,
    });
  };

  const getNewLessonTypeId = () => {
    try {
      return generateClassTypeId(newLessonType.category, newLessonType.durationMinutes);
    } catch (error) {
      return "";
    }
  };

  const addLessonType = () => {
    const classTypeId = getNewLessonTypeId();
    if (!classTypeId) {
      setErrorMessage("Nieprawidlowy kod typu lekcji.");
      return;
    }
    if (lessonTypes.types.some(type => type.classTypeId === classTypeId)) {
      setErrorMessage(`Typ lekcji ${classTypeId} juz istnieje.`);
      return;
    }

    const lessonType = {
      classTypeId,
      category: newLessonType.category,
      name: newLessonType.name || classTypeId,
      durationMinutes: newLessonType.durationMinutes,
      minStudents: newLessonType.minStudents,
      maxStudents: newLessonType.maxStudents,
      status: "ACTIVE",
    };

    setLessonTypes(normalizeLessonTypesSetting({
      ...lessonTypes,
      types: [...lessonTypes.types, lessonType],
    }));
    setNewLessonType(DEFAULT_NEW_LESSON_TYPE);
    setErrorMessage("");
  };

  const save = (sectionName, setting, successText) => {
    setSavingSection(sectionName);
    setSuccessMessage("");
    setErrorMessage("");
    return SettingsService.saveSetting(setting)
      .then((savedSetting) => {
        if (savedSetting.id === "paymentReminderRules") setPaymentRules(savedSetting);
        if (savedSetting.id === "schoolContact") setSchoolContact(savedSetting);
        if (savedSetting.id === "cancellationPolicy") setCancellationPolicy(savedSetting);
        if (savedSetting.id === "dashboardPreferences") setDashboardPreferences(savedSetting);
        if (savedSetting.id === "packagePolicy") setPackagePolicy(savedSetting);
        if (savedSetting.id === "lessonTypes") setLessonTypes(normalizeLessonTypesSetting(savedSetting));
        setSuccessMessage(successText);
      })
      .catch(() => {
        setErrorMessage("Nie udalo sie zapisac ustawien.");
      })
      .finally(() => setSavingSection(""));
  };

  if (loading) {
    return <div className="manage-text">Ladowanie ustawien...</div>;
  }

  return (
    <div>
      <h2 className="manage-title">Ustawienia</h2>

      {successMessage && <div className="alert alert-success" role="alert">{successMessage}</div>}
      {errorMessage && <div className="alert alert-danger" role="alert">{errorMessage}</div>}

      <div className="card mb-3">
        <div className="card-body">
          <h5 className="card-title">Przypomnienia o platnosciach</h5>
          <div className="form-group form-check">
            <input
              id="payment-reminders-enabled"
              className="form-check-input"
              type="checkbox"
              name="enabled"
              checked={paymentRules.enabled}
              onChange={handlePaymentChange}
            />
            <label className="form-check-label" htmlFor="payment-reminders-enabled">Wlacz wysylke przypomnien</label>
          </div>
          <div className="form-row">
            <div className="form-group col-md-4">
              <label htmlFor="firstOverdueReminderDaysAfterDueDate">Pierwsze przypomnienie po terminie</label>
              <input
                id="firstOverdueReminderDaysAfterDueDate"
                className="form-control"
                type="number"
                min="1"
                name="firstOverdueReminderDaysAfterDueDate"
                value={paymentRules.firstOverdueReminderDaysAfterDueDate}
                onChange={handlePaymentChange}
              />
            </div>
            <div className="form-group col-md-4">
              <label htmlFor="nextOverdueReminderDaysAfterPrevious">Kolejne przypomnienia co dni</label>
              <input
                id="nextOverdueReminderDaysAfterPrevious"
                className="form-control"
                type="number"
                min="1"
                name="nextOverdueReminderDaysAfterPrevious"
                value={paymentRules.nextOverdueReminderDaysAfterPrevious}
                onChange={handlePaymentChange}
              />
            </div>
            <div className="form-group col-md-4">
              <label htmlFor="maxOverdueReminders">Maksymalna liczba przypomnien</label>
              <input
                id="maxOverdueReminders"
                className="form-control"
                type="number"
                min="1"
                name="maxOverdueReminders"
                value={paymentRules.maxOverdueReminders}
                onChange={handlePaymentChange}
              />
            </div>
          </div>
          <button
            type="button"
            className="btn manage-button manage-red-button"
            disabled={savingSection === "payment"}
            onClick={() => save("payment", paymentRules, "Ustawienia przypomnien zostaly zapisane.")}
          >
            Zapisz przypomnienia
          </button>
        </div>
      </div>

      <div className="card mb-3">
        <div className="card-body">
          <h5 className="card-title">Kontakt szkoly</h5>
          <div className="form-row">
            <div className="form-group col-md-6">
              <label htmlFor="schoolContactEmail">Email</label>
              <input
                id="schoolContactEmail"
                className="form-control"
                type="email"
                name="email"
                value={schoolContact.email}
                onChange={handleSchoolContactChange}
              />
            </div>
            <div className="form-group col-md-6">
              <label htmlFor="schoolContactPhone">Telefon</label>
              <input
                id="schoolContactPhone"
                className="form-control"
                type="text"
                name="phone"
                value={schoolContact.phone}
                onChange={handleSchoolContactChange}
              />
            </div>
          </div>
          <div className="form-group">
            <label htmlFor="schoolContactAddress">Adres</label>
            <input
              id="schoolContactAddress"
              className="form-control"
              type="text"
              name="address"
              value={schoolContact.address}
              onChange={handleSchoolContactChange}
            />
          </div>
          <div className="form-group">
            <label htmlFor="schoolContactMapsUrl">Link do mapy</label>
            <input
              id="schoolContactMapsUrl"
              className="form-control"
              type="text"
              name="mapsUrl"
              value={schoolContact.mapsUrl}
              onChange={handleSchoolContactChange}
            />
          </div>
          <button
            type="button"
            className="btn manage-button manage-red-button"
            disabled={savingSection === "schoolContact"}
            onClick={() => save("schoolContact", schoolContact, "Dane kontaktowe zostaly zapisane.")}
          >
            Zapisz kontakt
          </button>
        </div>
      </div>

      <div className="card mb-3">
        <div className="card-body">
          <h5 className="card-title">Polityka odwolania zajec</h5>
          <div className="form-group form-check">
            <input
              id="cancellation-policy-enabled"
              className="form-check-input"
              type="checkbox"
              name="enabled"
              checked={cancellationPolicy.enabled}
              onChange={handleCancellationPolicyChange}
            />
            <label className="form-check-label" htmlFor="cancellation-policy-enabled">Wlacz polityke odwolania</label>
          </div>
          <div className="form-group">
            <label htmlFor="cancellationCutoffHours">Bezkosztowe odwolanie do ilu godzin przed lekcja</label>
            <input
              id="cancellationCutoffHours"
              className="form-control"
              type="number"
              min="1"
              name="cancellationCutoffHours"
              value={cancellationPolicy.cancellationCutoffHours}
              onChange={handleCancellationPolicyChange}
            />
          </div>
          <button
            type="button"
            className="btn manage-button manage-red-button"
            disabled={savingSection === "cancellationPolicy"}
            onClick={() => save("cancellationPolicy", cancellationPolicy, "Polityka odwolania zostala zapisana.")}
          >
            Zapisz polityke odwolania
          </button>
        </div>
      </div>

      <div className="card mb-3">
        <div className="card-body">
          <h5 className="card-title">Dashboard</h5>
          <div className="form-group">
            <label htmlFor="returnStateTtlHours">Pamiec dashboardu w godzinach</label>
            <input
              id="returnStateTtlHours"
              className="form-control"
              type="number"
              min="1"
              name="returnStateTtlHours"
              value={dashboardPreferences.returnStateTtlHours}
              onChange={handleDashboardPreferencesChange}
            />
          </div>
          <button
            type="button"
            className="btn manage-button manage-red-button"
            disabled={savingSection === "dashboardPreferences"}
            onClick={() => save("dashboardPreferences", dashboardPreferences, "Ustawienia dashboardu zostaly zapisane.")}
          >
            Zapisz dashboard
          </button>
        </div>
      </div>

      <div className="card mb-3">
        <div className="card-body">
          <h5 className="card-title">Pakiety</h5>
          <div className="form-row">
            <div className="form-group col-md-4">
              <label htmlFor="pendingPaymentDeadlineDays">
                Ile dni pakiet czeka na platnosc
                <input
                  id="pendingPaymentDeadlineDays"
                  className="form-control"
                  type="number"
                  min="1"
                  name="pendingPaymentDeadlineDays"
                  value={packagePolicy.pendingPaymentDeadlineDays}
                  onChange={handlePackagePolicyChange}
                />
              </label>
            </div>
            <div className="form-group col-md-4">
              <label htmlFor="pendingPaymentReminderDaysBeforeDue">
                Przypomnienie przed usunieciem ile dni
                <input
                  id="pendingPaymentReminderDaysBeforeDue"
                  className="form-control"
                  type="number"
                  min="1"
                  name="pendingPaymentReminderDaysBeforeDue"
                  value={packagePolicy.pendingPaymentReminderDaysBeforeDue}
                  onChange={handlePackagePolicyChange}
                />
              </label>
            </div>
            <div className="form-group col-md-4">
              <label htmlFor="endingSoonThresholdUnits">
                Prog konczacego sie pakietu
                <input
                  id="endingSoonThresholdUnits"
                  className="form-control"
                  type="number"
                  min="1"
                  name="endingSoonThresholdUnits"
                  value={packagePolicy.endingSoonThresholdUnits}
                  onChange={handlePackagePolicyChange}
                />
              </label>
            </div>
          </div>
          <div className="form-group form-check">
            <label className="form-check-label" htmlFor="emailStudentOnEndingPackage">
              <input
                id="emailStudentOnEndingPackage"
                className="form-check-input"
                type="checkbox"
                name="emailStudentOnEndingPackage"
                checked={packagePolicy.emailStudentOnEndingPackage}
                onChange={handlePackagePolicyChange}
              />
              Wyslij powiadomienie takze do ucznia
            </label>
          </div>
          <button
            type="button"
            className="btn manage-button manage-red-button"
            disabled={savingSection === "packagePolicy"}
            onClick={() => save("packagePolicy", packagePolicy, "Ustawienia pakietow zostaly zapisane.")}
          >
            Zapisz pakiety
          </button>
        </div>
      </div>

      <div className="card mb-3">
        <div className="card-body">
          <h5 className="card-title">Typy lekcji</h5>
          {lessonTypes.types.map((lessonType, index) => (
            <div className="form-row align-items-end" key={lessonType.classTypeId}>
              <div className="form-group col-md-2">
                <label htmlFor={`lesson-type-${lessonType.classTypeId}-id`}>Kod</label>
                <input
                  id={`lesson-type-${lessonType.classTypeId}-id`}
                  className="form-control"
                  type="text"
                  value={lessonType.classTypeId}
                  readOnly
                />
              </div>
              <div className="form-group col-md-4">
                <label htmlFor={`lesson-type-${lessonType.classTypeId}-name`}>Nazwa</label>
                <input
                  id={`lesson-type-${lessonType.classTypeId}-name`}
                  className="form-control"
                  type="text"
                  value={lessonType.name}
                  onChange={event => handleLessonTypeChange(index, "name", event.target.value)}
                />
              </div>
              <div className="form-group col-md-2">
                <label htmlFor={`lesson-type-${lessonType.classTypeId}-durationMinutes`}>Minuty</label>
                <input
                  id={`lesson-type-${lessonType.classTypeId}-durationMinutes`}
                  className="form-control"
                  type="number"
                  min="1"
                  value={lessonType.durationMinutes}
                  readOnly
                />
              </div>
              <div className="form-group col-md-1">
                <label htmlFor={`lesson-type-${lessonType.classTypeId}-min`}>Min.</label>
                <input
                  id={`lesson-type-${lessonType.classTypeId}-min`}
                  className="form-control"
                  type="number"
                  min="1"
                  value={lessonType.minStudents}
                  onChange={event => handleLessonTypeChange(index, "minStudents", event.target.value)}
                />
              </div>
              <div className="form-group col-md-1">
                <label htmlFor={`lesson-type-${lessonType.classTypeId}-max`}>Max.</label>
                <input
                  id={`lesson-type-${lessonType.classTypeId}-max`}
                  className="form-control"
                  type="number"
                  min="1"
                  value={lessonType.maxStudents}
                  onChange={event => handleLessonTypeChange(index, "maxStudents", event.target.value)}
                />
              </div>
              <div className="form-group col-md-2 form-check">
                <input
                  id={`lesson-type-${lessonType.classTypeId}-active`}
                  name={`lessonTypeStatus-${lessonType.classTypeId}`}
                  className="form-check-input"
                  type="checkbox"
                  checked={lessonType.status === "ACTIVE"}
                  onChange={event => handleLessonTypeChange(index, "status", event.target.checked)}
                />
                <label className="form-check-label" htmlFor={`lesson-type-${lessonType.classTypeId}-active`}>
                  Aktywny {lessonType.classTypeId}
                </label>
              </div>
            </div>
          ))}
          <div className="border-top pt-3 mt-2">
            <div className="form-row align-items-end">
              <div className="form-group col-md-2">
                <label htmlFor="newLessonTypeCategory">Kategoria nowego typu lekcji</label>
                <select
                  id="newLessonTypeCategory"
                  name="category"
                  className="form-control"
                  value={newLessonType.category}
                  onChange={handleNewLessonTypeChange}
                >
                  <option value="G">G</option>
                  <option value="I">I</option>
                  <option value="D">D</option>
                  <option value="O">O</option>
                </select>
              </div>
              <div className="form-group col-md-2">
                <label htmlFor="newLessonTypeDurationMinutes">Czas trwania nowego typu lekcji</label>
                <input
                  id="newLessonTypeDurationMinutes"
                  name="durationMinutes"
                  className="form-control"
                  type="number"
                  min="1"
                  value={newLessonType.durationMinutes}
                  onChange={handleNewLessonTypeChange}
                />
              </div>
              <div className="form-group col-md-2">
                <label htmlFor="newLessonTypeId">Kod</label>
                <input
                  id="newLessonTypeId"
                  className="form-control"
                  type="text"
                  value={getNewLessonTypeId()}
                  readOnly
                />
              </div>
              <div className="form-group col-md-3">
                <label htmlFor="newLessonTypeName">Nazwa nowego typu lekcji</label>
                <input
                  id="newLessonTypeName"
                  name="name"
                  className="form-control"
                  type="text"
                  value={newLessonType.name}
                  onChange={handleNewLessonTypeChange}
                />
              </div>
              <div className="form-group col-md-1">
                <label htmlFor="newLessonTypeMinStudents">Min.</label>
                <input
                  id="newLessonTypeMinStudents"
                  name="minStudents"
                  className="form-control"
                  type="number"
                  min="1"
                  value={newLessonType.minStudents}
                  onChange={handleNewLessonTypeChange}
                />
              </div>
              <div className="form-group col-md-1">
                <label htmlFor="newLessonTypeMaxStudents">Max.</label>
                <input
                  id="newLessonTypeMaxStudents"
                  name="maxStudents"
                  className="form-control"
                  type="number"
                  min="1"
                  value={newLessonType.maxStudents}
                  onChange={handleNewLessonTypeChange}
                />
              </div>
              <div className="form-group col-md-1">
                <button
                  type="button"
                  className="btn manage-button manage-teal-button btn-block"
                  onClick={addLessonType}
                >
                  Dodaj typ lekcji
                </button>
              </div>
            </div>
          </div>
          <button
            type="button"
            className="btn manage-button manage-red-button"
            disabled={savingSection === "lessonTypes"}
            onClick={() => save("lessonTypes", lessonTypes, "Typy lekcji zostaly zapisane.")}
          >
            Zapisz typy lekcji
          </button>
        </div>
      </div>
    </div>
  );
};

export default Settings;
